<template>
    <div class="mp-root" role="group" aria-label="Month picker" tabindex="0" @keydown.stop.prevent="onKeydown">
        <!-- Prev -->
        <button class="mp-chevron-btn" :class="{ 'is-disabled': !canPrev }" :disabled="!canPrev" @click="addMonths(-1)" aria-label="Previous month" type="button">
            <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8.10823 0.172851L9.69922 1.76384L3.18221 8.28085L9.69922 14.7979L8.10823 16.3888L0.000232342 8.28085L8.10823 0.172851Z" fill="currentColor" />
            </svg>
        </button>

        <!-- Label -->
        <div class="mp-label" :title="label">
            {{ label }}
        </div>

        <!-- Next -->

        <button class="mp-chevron-btn" :class="{ 'is-disabled': !canNext }" :disabled="!canNext" @click="addMonths(1)" aria-label="Next month" type="button">
            <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.59099 16.8271L-6.95444e-08 15.2362L6.517 8.71915L-6.39279e-07 2.20215L1.59099 0.611156L9.69899 8.71915L1.59099 16.8271Z" fill="currentColor" />
            </svg>
        </button>
    </div>
</template>

<script>
/**
 * MonthPicker (Vue 2)
 * v-model: { month: 1..12, year: number }
 */
export default {
    name: 'MonthPicker',
    model: { prop: 'value', event: 'input' },

    props: {
        value: {
            type: Object, // { month: 1..12, year: 20xx }
            default: null,
        },
        min: {
            // Giới hạn nhỏ nhất (nhận Date hoặc {month, year})
            type: [Object, Date],
            default: null,
        },
        max: {
            // Giới hạn lớn nhất (nhận Date hoặc {month, year})
            type: [Object, Date],
            default: null,
        },

        displayFormat: {
            type: Function,
            // range = { month: 1..12, year }, ctx = this
            default: (range, ctx) => {
                if (!range) return '';
                const { month, year } = range;
                // Capitalize đầu từ nếu tháng lấy từ i18n thấp chữ
                const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
                const name = ctx.monthNames[month - 1] || '';
                return `${cap(name)} ${year}`;
            },
        },
        // Nếu true: tự động "kẹp" giá trị value về trong [min, max] khi mount/watch
        clampToRange: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        const now = new Date();
        const init = this.value && this._validMY(this.value)
            ? this._cloneMY(this.value)
            : { month: now.getMonth() + 1, year: now.getFullYear() };

        return {
            current: init, // {month, year}
        };
    },

    computed: {
        normalizedMin() {
            return this.min ? this._toMY(this.min) : null;
        },
        normalizedMax() {
            return this.max ? this._toMY(this.max) : null;
        },
        monthNames() {
            const fmt = new Intl.DateTimeFormat(this.$i18n.locale, { month: 'long' })
            return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2020, i, 1)))
        },
        label() {
            return this.displayFormat(this.current, this);
        },
        canPrev() {
            if (!this.normalizedMin) return true;
            return this._compareMY(this._addMonths(this.current, -1), this.normalizedMin) >= 0;
        },
        canNext() {
            if (!this.normalizedMax) return true;
            return this._compareMY(this._addMonths(this.current, +1), this.normalizedMax) <= 0;
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(v) {
                if (v && this._validMY(v)) {
                    const next = this.clampToRange ? this._clamp(v) : this._cloneMY(v);
                    if (this._compareMY(next, this.current) !== 0) {
                        this.current = next;
                    }
                }
            },
        },
        // Khi min/max đổi, đảm bảo current nằm trong range nếu clampToRange
        min() {
            if (!this.clampToRange) return;
            const clamped = this._clamp(this.current);
            if (this._compareMY(clamped, this.current) !== 0) {
                this.current = clamped;
                this._emit();
            }
        },
        max() {
            if (!this.clampToRange) return;
            const clamped = this._clamp(this.current);
            if (this._compareMY(clamped, this.current) !== 0) {
                this.current = clamped;
                this._emit();
            }
        },
    },

    methods: {
        // ==== Public actions ====
        addMonths(delta) {
            const next = this._addMonths(this.current, delta);
            if (this.normalizedMin && this._compareMY(next, this.normalizedMin) < 0) return;
            if (this.normalizedMax && this._compareMY(next, this.normalizedMax) > 0) return;
            this.current = next;
            this._emit();
        },

        // Keyboard: ←/→ tháng, PgUp/PgDn năm, Home=Jan, End=Dec
        onKeydown(e) {
            switch (e.key) {
                case 'ArrowLeft': return this.addMonths(-1);
                case 'ArrowRight': return this.addMonths(+1);
                case 'PageUp': return this.addMonths(-12);
                case 'PageDown': return this.addMonths(+12);
                case 'Home': return this._setMonth(1);
                case 'End': return this._setMonth(12);
            }
        },

        _setMonth(m) {
            const next = { year: this.current.year, month: m };
            const clamped = this._clamp(next);
            if (this._compareMY(clamped, this.current) === 0) return;
            this.current = clamped;
            this._emit();
        },

        _emit() {
            const payload = this._cloneMY(this.current);
            this.$emit('input', payload);
            this.$emit('change', payload);
        },

        // ==== Helpers (MY = {month, year}) ====
        _validMY(v) {
            return v && Number.isInteger(v.month) && Number.isInteger(v.year)
                && v.month >= 1 && v.month <= 12;
        },
        _cloneMY(v) {
            return { month: v.month, year: v.year };
        },
        _toMY(d) {
            if (d instanceof Date) {
                return { month: d.getMonth() + 1, year: d.getFullYear() };
            }
            if (this._validMY(d)) return this._cloneMY(d);
            // fallback: now
            const now = new Date();
            return { month: now.getMonth() + 1, year: now.getFullYear() };
        },
        _compareMY(a, b) {
            // a>b => 1, a==b => 0, a<b => -1
            if (a.year !== b.year) return a.year > b.year ? 1 : -1;
            if (a.month !== b.month) return a.month > b.month ? 1 : -1;
            return 0;
        },
        _addMonths(my, delta) {
            const total = (my.year * 12 + (my.month - 1)) + delta;
            const y = Math.floor(total / 12);
            const m = (total % 12 + 12) % 12; // 0..11
            return { year: y, month: m + 1 };
        },
        _clamp(my) {
            let out = this._cloneMY(my);
            if (this.normalizedMin && this._compareMY(out, this.normalizedMin) < 0) {
                out = this._cloneMY(this.normalizedMin);
            }
            if (this.normalizedMax && this._compareMY(out, this.normalizedMax) > 0) {
                out = this._cloneMY(this.normalizedMax);
            }
            return out;
        },
    },
};
</script>

<style scoped>
.mp-root {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    border: 1px solid #dcdfe6;
    border-radius: 10px;
    background: #fff;
    /* nhẹ nhàng giống ảnh */
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
    user-select: none;
    outline: none;
}

.mp-root:focus {
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.15);
    border-color: #a0cfff;
}

.mp-chevron-btn {
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    background: #FFFFFF;
    color: #111;
    border-radius: 8px;
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
}

.mp-chevron-btn:hover:not(.is-disabled) {
    background: #ebeef5;
}

.mp-chevron-btn.is-disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.mp-label {
    font-size: 14px;
    font-weight: 500;
    color: #111;
    min-width: 110px;
    text-align: center;
}
</style>